import * as THREE from 'three'
import PLACES from '@/assets/environment/road-label-places.json'

/**
 * 路名标牌：参考平面图上的「解放东路 / 杭海路 / 秋涛北路 / 秋涛路 / 秋园二路 / 清江路」。
 *
 * 观感目标（用户 2026-10-27 反馈）：
 *   · 像高德一样"贴在路上"，字号大、白描边、无底板；
 *   · **不要整块文字一起倾斜**。每个汉字是独立的 Sprite，永远正立，
 *     整串字的位置沿道路的屏幕投影方向逐个排开 —— 斜街是一串正立字，
 *     竖街是竖着叠一列，整体仍贴在路面上。
 *
 * 每个字单独一张小贴图 + 一个 Sprite，同一字符共用贴图/材质；
 * 具体怎么沿路排、屏显多大，在 sceneView.layoutRoadLabels 里每帧算。
 */
const STYLE = {
  screenHeightPx: 46, // 单个字的屏显高度（含描边留白）
  fontSize: 96, // 画布字号：屏显大小由 screenHeightPx 决定，这里只管纹理清晰度
  // 颜色按「ACES + 曝光 2.45 之后的输出」配：字芯输出深蓝灰，描边输出近白。
  textColor: '#0b2a44',
  haloColor: 'rgba(232, 247, 255, 0.95)',
  glowColor: 'rgba(110, 190, 255, 0.35)',
  glowW: 0.15, // 外发光描边宽（相对字号）
  haloW: 0.09, // 白描边宽（相对字号）
}

export const ROAD_LABEL_SCREEN_HEIGHT_PX = STYLE.screenHeightPx
/**
 * 相邻两个字的屏幕间距 = 单字屏显高度 × 该系数 ÷ 字链方向的主轴分量。
 * 水平/竖排时主轴=1，间距约 0.68 字高；45° 斜排时主轴=0.707，
 * 间距约 0.96 字高，保证两个正立字不会在斜线上压在一起。
 */
export const ROAD_LABEL_CHAR_STEP = 0.68
/** 路名比楼名晚一档出现：全景缩到最小时地面信息已经够密了 */
export const ROAD_LABEL_MIN_ZOOM = 16.8
/** 字链中心离地高度（米）：贴着路面但不参与深度测试，隔着楼也能读到 */
const ROAD_LABEL_Z = 3.0

const FONT_OF = (px) => `700 ${Math.round(px)}px "Microsoft YaHei", "PingFang SC", sans-serif`

function makeCharTexture(ch) {
  const S = STYLE
  const canvas = document.createElement('canvas')
  let ctx = canvas.getContext('2d')
  ctx.font = FONT_OF(S.fontSize)
  const charW = ctx.measureText(ch).width
  const pad = Math.max(8, Math.round(S.fontSize * 0.26))
  const textH = S.fontSize * 1.2
  canvas.width = Math.round(charW + pad * 2)
  canvas.height = Math.round(textH + pad * 2)

  ctx = canvas.getContext('2d')
  ctx.font = FONT_OF(S.fontSize)
  ctx.textAlign = 'center'
  ctx.textBaseline = 'alphabetic'
  ctx.lineJoin = 'round'
  ctx.miterLimit = 2

  // 按字形实际边界把字居中，避免"路"这种左右结构偏上/偏下
  const m = ctx.measureText(ch)
  const baseline =
    m.actualBoundingBoxAscent !== undefined
      ? canvas.height / 2 + (m.actualBoundingBoxAscent - m.actualBoundingBoxDescent) / 2
      : canvas.height / 2 + S.fontSize * 0.34

  ctx.lineWidth = Math.max(1, S.fontSize * S.glowW)
  ctx.strokeStyle = S.glowColor
  ctx.strokeText(ch, canvas.width / 2, baseline)
  ctx.lineWidth = Math.max(1, S.fontSize * S.haloW)
  ctx.strokeStyle = S.haloColor
  ctx.strokeText(ch, canvas.width / 2, baseline)
  ctx.fillStyle = S.textColor
  ctx.fillText(ch, canvas.width / 2, baseline)

  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.needsUpdate = true
  return { texture, aspect: canvas.width / canvas.height }
}

/**
 * @returns {{group:THREE.Group, items:Array<{
 *   name:string, bearing:number, anchor:THREE.Vector3,
 *   chars:Array<{sprite:THREE.Sprite, aspect:number, ch:string}>,
 *   visible:boolean, onScreen:boolean, screenX:number, screenY:number,
 *   screenRect:{x0:number,x1:number,y0:number,y1:number}|null
 * }>}}
 */
export function createRoadLabels(places = PLACES.places) {
  const group = new THREE.Group()
  group.name = 'roadLabels'

  /** 字符 -> { material }；同一条路上的重复字（"路"）共用一张贴图和一个材质 */
  const charAssets = new Map()
  const assetOf = (ch) => {
    let a = charAssets.get(ch)
    if (!a) {
      const { texture, aspect } = makeCharTexture(ch)
      a = {
        aspect,
        material: new THREE.SpriteMaterial({
          map: texture,
          transparent: true,
          depthWrite: false,
          // 路名和楼名同属信息层：不做深度测试，隔着楼也能读到。
          // 层级靠 renderOrder 压在楼名之下（900 < 998/999），被楼名挡住时优先保楼名。
          depthTest: false,
          toneMapped: false,
        }),
      }
      charAssets.set(ch, a)
    }
    return a
  }

  const items = places.map((p) => {
    const chars = [...String(p.name)].map((ch) => {
      const asset = assetOf(ch)
      const sprite = new THREE.Sprite(asset.material)
      // 以字心为锚：沿道路逐字排布时每个字都绕自己的中心，不会甩出路面
      sprite.center.set(0.5, 0.5)
      sprite.renderOrder = 900
      sprite.name = `roadChar_${p.name}_${ch}`
      sprite.position.set(p.east, p.north, ROAD_LABEL_Z)
      sprite.userData = { labelKind: 'road', roadName: p.name, char: ch }
      sprite.raycast = () => {} // 不参与建筑拾取：点路名不应选中它背后的楼
      group.add(sprite)
      return { sprite, aspect: asset.aspect, ch }
    })
    return {
      name: p.name,
      bearing: p.bearing,
      anchor: new THREE.Vector3(p.east, p.north, ROAD_LABEL_Z),
      chars,
      visible: true,
      onScreen: true,
      screenX: 0,
      screenY: 0,
      screenRect: null,
    }
  })

  return { group, items }
}
